import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

/**
 * BW11 — OTP-first sign-in.
 *
 * Magic links are unreliable in the iOS PWA (Safari intercepts them and
 * either drops them outside the standalone window or stores the resulting
 * session in the wrong storage scope). OTP avoids that whole class of
 * problem: user receives a 6-digit code, types it into the PWA itself.
 *
 * Magic link is kept available behind a "Use email link instead" toggle
 * for ~30 days as a fallback while the new flow proves itself.
 *
 * Long-lived session (90d JWT, 365d refresh) configured at the project
 * level — once verified we don't have to do this again for ~3 months per
 * device.
 */

type OtpState =
  | { kind: "email" }
  | { kind: "sending" }
  | { kind: "code-entry"; email: string }
  | { kind: "verifying"; email: string }
  | { kind: "error"; message: string; from: "email" | "code"; email?: string }
  | { kind: "magic-sent"; email: string };

export function SignIn() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [useMagic, setUseMagic] = useState(false);
  const [state, setState] = useState<OtpState>({ kind: "email" });

  async function sendCode(opts?: { resend?: boolean }) {
    if (!email) return;
    setState({ kind: "sending" });
    const redirectTo =
      typeof window !== "undefined"
        ? `${window.location.origin}/auth/callback`
        : undefined;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // shouldCreateUser=true keeps the bootstrap path open for the very
        // first sign-in; project-level new-user lockdown is handled in
        // Supabase config, not here.
        shouldCreateUser: true,
        // emailRedirectTo is only consulted by the magic-link template; OTP
        // codes ignore it. Keeping it set means the magic-link fallback
        // still works without changing the email template again.
        emailRedirectTo: redirectTo,
      },
    });
    if (error) {
      setState({ kind: "error", message: error.message, from: "email" });
    } else if (useMagic) {
      setState({ kind: "magic-sent", email });
    } else {
      if (!opts?.resend) setCode("");
      setState({ kind: "code-entry", email });
    }
  }

  async function verifyCode() {
    if (state.kind !== "code-entry" && state.kind !== "error") return;
    const targetEmail = state.kind === "code-entry" ? state.email : state.email ?? email;
    if (!targetEmail || code.length !== 6) return;
    setState({ kind: "verifying", email: targetEmail });
    const { error } = await supabase.auth.verifyOtp({
      email: targetEmail,
      token: code,
      type: "email",
    });
    if (error) {
      setState({
        kind: "error",
        message: error.message,
        from: "code",
        email: targetEmail,
      });
    } else {
      navigate("/", { replace: true });
    }
  }

  function startOver() {
    setCode("");
    setState({ kind: "email" });
  }

  const isCodeView =
    state.kind === "code-entry" ||
    state.kind === "verifying" ||
    (state.kind === "error" && state.from === "code");

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg)",
        color: "var(--fg)",
        padding: "var(--space-6)",
      }}
    >
      <main
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--bg-2)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-surface)",
          padding: "var(--space-10) var(--space-8)",
        }}
      >
        <header style={{ marginBottom: "var(--space-8)" }}>
          <span
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: 32,
              color: "var(--fg)",
            }}
          >
            Haus
          </span>
          <h1
            style={{
              fontSize: "var(--type-display-m)",
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              marginTop: "var(--space-3)",
            }}
          >
            Sign in
          </h1>
          <p
            style={{
              color: "var(--fg-2)",
              fontSize: "var(--type-body-s)",
              marginTop: "var(--space-2)",
              lineHeight: 1.5,
            }}
          >
            {useMagic
              ? "We'll email you a sign-in link."
              : "We'll email you a 6-digit code. Type it back here — works even from a saved-to-Home Screen PWA."}{" "}
            Once verified, your session lasts ~90 days.
          </p>
        </header>

        {state.kind === "magic-sent" ? (
          <p
            role="status"
            style={statusBox()}
          >
            Magic link sent to <strong>{state.email}</strong>. Check your inbox.
          </p>
        ) : isCodeView ? (
          <CodeStep
            email={state.kind === "error" ? state.email ?? "" : state.email}
            code={code}
            setCode={setCode}
            verifying={state.kind === "verifying"}
            errorMessage={state.kind === "error" && state.from === "code" ? state.message : null}
            onVerify={verifyCode}
            onResend={() => sendCode({ resend: true })}
            onStartOver={startOver}
          />
        ) : (
          <EmailStep
            email={email}
            setEmail={setEmail}
            useMagic={useMagic}
            setUseMagic={setUseMagic}
            sending={state.kind === "sending"}
            errorMessage={state.kind === "error" && state.from === "email" ? state.message : null}
            onSubmit={() => sendCode()}
          />
        )}

        <p
          style={{
            marginTop: "var(--space-8)",
            fontSize: "var(--type-meta)",
            color: "var(--fg-3)",
            lineHeight: 1.5,
          }}
        >
          v1 is single-user. New sign-ups are disabled at the project level.
        </p>
      </main>
    </div>
  );
}

function EmailStep({
  email,
  setEmail,
  useMagic,
  setUseMagic,
  sending,
  errorMessage,
  onSubmit,
}: {
  email: string;
  setEmail: (v: string) => void;
  useMagic: boolean;
  setUseMagic: (v: boolean) => void;
  sending: boolean;
  errorMessage: string | null;
  onSubmit: () => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!sending) onSubmit();
      }}
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
    >
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-2)",
          fontSize: "var(--type-body-s)",
          color: "var(--fg-2)",
        }}
      >
        Email
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          style={inputStyle()}
        />
      </label>

      <button
        type="submit"
        disabled={sending || !email}
        style={primaryButtonStyle(sending || !email, sending)}
      >
        {sending ? "Sending…" : useMagic ? "Send magic link" : "Send code"}
      </button>

      <button
        type="button"
        onClick={() => setUseMagic(!useMagic)}
        style={{
          background: "transparent",
          border: "none",
          color: "var(--fg-3)",
          fontSize: "var(--type-meta)",
          cursor: "pointer",
          textAlign: "center",
          padding: 0,
          textDecoration: "underline",
          textDecorationColor: "var(--hair-2)",
        }}
      >
        {useMagic ? "Use 6-digit code instead" : "Use email link instead"}
      </button>

      {errorMessage && (
        <p role="alert" style={errorStyle()}>
          {errorMessage}
        </p>
      )}
    </form>
  );
}

function CodeStep({
  email,
  code,
  setCode,
  verifying,
  errorMessage,
  onVerify,
  onResend,
  onStartOver,
}: {
  email: string;
  code: string;
  setCode: (v: string) => void;
  verifying: boolean;
  errorMessage: string | null;
  onVerify: () => void;
  onResend: () => void;
  onStartOver: () => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  // Auto-submit when 6 digits typed.
  useEffect(() => {
    if (!verifying && code.length === 6) {
      onVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  function onChange(raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!verifying && code.length === 6) onVerify();
      }}
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
    >
      <p
        style={{
          margin: 0,
          fontSize: "var(--type-body-s)",
          color: "var(--fg-2)",
          lineHeight: 1.5,
        }}
      >
        We sent a code to <strong style={{ color: "var(--fg)" }}>{email}</strong>. Codes expire in 10 minutes.
      </p>

      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-2)",
          fontSize: "var(--type-body-s)",
          color: "var(--fg-2)",
        }}
      >
        6-digit code
        <input
          ref={ref}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          pattern="[0-9]{6}"
          maxLength={6}
          value={code}
          onChange={(e) => onChange(e.target.value)}
          placeholder="000000"
          style={{
            ...inputStyle(),
            fontFamily: "var(--font-mono)",
            fontSize: 22,
            letterSpacing: "0.4em",
            textAlign: "center",
            height: 56,
          }}
        />
      </label>

      <button
        type="submit"
        disabled={verifying || code.length !== 6}
        style={primaryButtonStyle(verifying || code.length !== 6, verifying)}
      >
        {verifying ? "Verifying…" : "Verify"}
      </button>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "var(--space-2)",
          fontSize: "var(--type-meta)",
        }}
      >
        <button
          type="button"
          onClick={onResend}
          disabled={verifying}
          style={linkButtonStyle()}
        >
          Resend code
        </button>
        <button
          type="button"
          onClick={onStartOver}
          disabled={verifying}
          style={linkButtonStyle()}
        >
          Wrong email? Start over
        </button>
      </div>

      {errorMessage && (
        <p role="alert" style={errorStyle()}>
          {errorMessage}
        </p>
      )}
    </form>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    height: 40,
    padding: "0 var(--space-3)",
    background: "var(--bg-3)",
    color: "var(--fg)",
    border: "1px solid var(--hair-2)",
    borderRadius: "var(--radius-card)",
    fontFamily: "var(--font-sans)",
    fontSize: "var(--type-body)",
  };
}

function primaryButtonStyle(disabled: boolean, busy: boolean): React.CSSProperties {
  return {
    height: 44,
    padding: "0 var(--space-4)",
    background: "var(--accent)",
    color: "#0a0b0d",
    fontWeight: 600,
    fontSize: "var(--type-body)",
    borderRadius: "var(--radius-card)",
    border: "1px solid transparent",
    cursor: busy ? "wait" : disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
  };
}

function linkButtonStyle(): React.CSSProperties {
  return {
    background: "transparent",
    border: "none",
    color: "var(--fg-3)",
    cursor: "pointer",
    padding: 0,
    fontSize: "var(--type-meta)",
    textDecoration: "underline",
    textDecorationColor: "var(--hair-2)",
  };
}

function errorStyle(): React.CSSProperties {
  return {
    fontSize: "var(--type-body-s)",
    color: "var(--brick)",
    margin: 0,
  };
}

function statusBox(): React.CSSProperties {
  return {
    fontSize: "var(--type-body)",
    color: "var(--fg)",
    padding: "var(--space-4)",
    border: "1px solid var(--accent-dim)",
    borderRadius: "var(--radius-card)",
    background: "var(--accent-wash)",
  };
}
